import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import {
  Wrapper,
  Title,
  Subtitle,
  ProfilesGrid,
  ProfileCard,
  ActionButton,
  LogoImage,
} from "./styles";
import gpv from "../../../../assets/images/logo-georanking.png";
import useUserStore from "../../../../store/user";
import { IDashboardData } from "../../../dashboard/types";
import { DashboardService } from "../../../dashboard/services";

const extractName = (str: string) => {
  const parts = str.split(" - ");
  return parts[1]?.trim() || null;
};

const extractUUID = (str: string) => {
  const match = str.match(/[0-9a-fA-F-]{36}/);
  return match ? match[0] : "";
};

type ProfileItem = {
  id: string;
  name: string | null;
  accessLevel?: "VIEW" | "EDIT";
};

export const ProfileSelection: React.FC = () => {
  const { setUser, user, profiles, profileAccesses } = useUserStore();

  const [selectedProfileId, setSelectedProfileId] = useState<string>("");
  const navigate = useNavigate();

  // Preferir a lista estruturada (profileAccesses) retornada por /users/me;
  // fallback para o formato legado "publicId - nome" quando ela nao existir.
  const profileItems: ProfileItem[] =
    profileAccesses.length > 0
      ? profileAccesses.map((access) => ({
          id: access.profilePublicId,
          name: access.name,
          accessLevel: access.accessLevel,
        }))
      : profiles.map((profile) => ({
          id: extractUUID(profile),
          name: extractName(profile),
        }));

  const handleSelectProfile = (profileId: string) => {
    setSelectedProfileId(profileId);
  };

  const handleContinue = async () => {
    if (selectedProfileId) {
      // Lógica para continuar, por exemplo, salvar o perfil selecionado e navegar
      if (user) {
        // Usa o perfil ESCOLHIDO pelo usuario, nao o primeiro da lista.
        const profileLocal: IDashboardData | null =
          await DashboardService.getProfile(selectedProfileId, 7);

        if (
          profileLocal &&
          profileLocal?.status !== "WAITING_FIRST_PAYMENT" &&
          profileLocal?.status !== "LOGIN_PENDING" &&
          profileLocal?.status !== "WAITING_INITIAL_SYNC"
        ) {
          const selectedItem = profileItems.find(
            (item) => item.id === selectedProfileId,
          );
          setUser({
            ...user,
            name: user.name ?? "", // Ensure 'name' is always a string
            businessProfiles: [selectedProfileId],
            token: user.token ?? "", // Ensure token is always a string
            selectedProfileAccessLevel: selectedItem?.accessLevel,
          }); // Atualiza o perfil selecionado no estado global
          navigate("/dashboard");
        } else {
          navigate("/profile-setup");
        }
      }
    }
  };

  return (
    <Wrapper>
      <LogoImage src={gpv} alt="Logo" />
      <Title>Selecione um Perfil</Title>
      <Subtitle>
        Escolha o perfil de negócio que você deseja gerenciar.
      </Subtitle>
      <ProfilesGrid>
        {profileItems.map((item) => (
          <ProfileCard
            key={item.id}
            $isSelected={selectedProfileId === item.id}
            onClick={() => handleSelectProfile(item.id)}
          >
            <AccountCircleIcon className="icon" />
            <span className="name">{item.name}</span>
            {item.accessLevel && (
              <span style={{ fontSize: "12px", color: "#5f6368" }}>
                {item.accessLevel === "EDIT" ? "Edição" : "Visualização"}
              </span>
            )}
          </ProfileCard>
        ))}
      </ProfilesGrid>
      <ActionButton onClick={handleContinue} disabled={!selectedProfileId}>
        Continuar
      </ActionButton>
    </Wrapper>
  );
};
